/**
 * Inline SVG icon components for the ApprovalLogDrawer.
 * Paths are sourced from web-front/src/images/timesheets/ (Figma exports).
 * Colors are intentionally hardcoded to match the Figma design.
 */
import React from 'react';

type IconProps = {
	size?: number;
};

/** Green check-circle — used for APPROVE */
export const CheckCircleIcon: React.FC<IconProps> = ({size = 16}) => (
	<svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M4.67068 8.33733C4.85293 8.15509 5.1484 8.15509 5.33065 8.33733L6.66732 9.674L10.6707 5.67066C10.8529 5.48841 11.1484 5.48841 11.3306 5.67066C11.5129 5.8529 11.5129 6.14838 11.3306 6.33063L7.00044 10.6608C6.9994 10.6619 6.99836 10.6629 6.99731 10.664C6.88341 10.7779 6.72528 10.8206 6.5782 10.7921C6.52178 10.7812 6.46699 10.7598 6.41695 10.7279C6.38871 10.7099 6.36198 10.6886 6.33733 10.664L6.33516 10.6618L4.67068 8.9973C4.48844 8.81506 4.48844 8.51958 4.67068 8.33733Z"
			fill="#148F6A"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M8.00065 14.6673C11.6825 14.6673 14.6673 11.6825 14.6673 8.00065C14.6673 4.31875 11.6825 1.33398 8.00065 1.33398C4.31875 1.33398 1.33398 4.31875 1.33398 8.00065C1.33398 11.6825 4.31875 14.6673 8.00065 14.6673ZM8.00065 13.734C11.1671 13.734 13.734 11.1671 13.734 8.00065C13.734 4.83422 11.1671 2.26732 8.00065 2.26732C4.83422 2.26732 2.26732 4.83422 2.26732 8.00065C2.26732 11.1671 4.83422 13.734 8.00065 13.734Z"
			fill="#148F6A"
		/>
	</svg>
);

/** Red close-circle — used for DECLINE */
export const CloseCircleIcon: React.FC<IconProps> = ({size = 16}) => (
	<svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M5.33733 5.33733C5.51957 5.15508 5.81505 5.15508 5.99729 5.33733L8.00064 7.34068L10.004 5.33733C10.1862 5.15508 10.4817 5.15508 10.664 5.33733C10.8462 5.51957 10.8462 5.81505 10.664 5.99729L8.66061 8.00064L10.664 10.004C10.8462 10.1862 10.8462 10.4817 10.664 10.664C10.4817 10.8462 10.1862 10.8462 10.004 10.664L8.00064 8.66061L5.99729 10.664C5.81505 10.8462 5.51957 10.8462 5.33733 10.664C5.15508 10.4817 5.15508 10.1862 5.33733 10.004L7.34068 8.00064L5.33733 5.99729C5.15508 5.81505 5.15508 5.51957 5.33733 5.33733Z"
			fill="#DB0900"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M8.00065 14.6673C11.6825 14.6673 14.6673 11.6825 14.6673 8.00065C14.6673 4.31875 11.6825 1.33398 8.00065 1.33398C4.31875 1.33398 1.33398 4.31875 1.33398 8.00065C1.33398 11.6825 4.31875 14.6673 8.00065 14.6673ZM8.00065 13.734C11.1671 13.734 13.734 11.1671 13.734 8.00065C13.734 4.83422 11.1671 2.26732 8.00065 2.26732C4.83422 2.26732 2.26732 4.83422 2.26732 8.00065C2.26732 11.1671 4.83422 13.734 8.00065 13.734Z"
			fill="#DB0900"
		/>
	</svg>
);

/** Green progress-check circle — used for SUBMIT / RESUBMIT */
export const ProgressCheckIcon: React.FC<IconProps> = ({size = 16}) => (
	<svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M7.29745 1.30392C7.5288 1.27988 7.7635 1.26758 8.00091 1.26758C11.7196 1.26758 14.7343 4.28219 14.7343 8.00091C14.7343 11.7196 11.7196 14.7342 8.00091 14.7342C7.7635 14.7342 7.5288 14.7219 7.29745 14.6979C7.0411 14.6713 6.85487 14.4419 6.88151 14.1855C6.90814 13.9292 7.13755 13.7429 7.3939 13.7696C7.59327 13.7903 7.79579 13.8009 8.00093 13.8009C11.2042 13.8009 13.8009 11.2042 13.8009 8.00091C13.8009 4.79766 11.2042 2.20091 8.00091 2.20091C7.79577 2.20091 7.59327 2.21154 7.3939 2.23226C7.13755 2.25889 6.90814 2.07267 6.88151 1.81632C6.85487 1.55996 7.0411 1.33056 7.29745 1.30392Z"
			fill="#148F6A"
		/>
		<path
			d="M5.87755 2.08457C5.98251 2.31996 5.87678 2.59588 5.64139 2.70084C5.26932 2.86675 4.91782 3.07076 4.59185 3.308C4.38347 3.45966 4.09159 3.41367 3.93993 3.20528C3.78827 2.99689 3.83426 2.70502 4.04264 2.55336C4.42086 2.2781 4.82898 2.04118 5.26128 1.84841C5.49667 1.74345 5.77258 1.84918 5.87755 2.08457Z"
			fill="#148F6A"
		/>
		<path
			d="M3.308 4.59185C3.45966 4.38347 3.41367 4.09159 3.20528 3.93993C2.99689 3.78827 2.70502 3.83426 2.55336 4.04264C2.2781 4.42086 2.04118 4.82898 1.84842 5.26128C1.74345 5.49667 1.84918 5.77258 2.08457 5.87755C2.31996 5.98251 2.59588 5.87678 2.70084 5.64139C2.86675 5.26932 3.07076 4.91782 3.308 4.59185Z"
			fill="#148F6A"
		/>
		<path
			d="M1.81632 6.88151C2.07267 6.90814 2.25889 7.13755 2.23226 7.3939C2.21154 7.59327 2.20091 7.79577 2.20091 8.00091C2.20091 8.20605 2.21154 8.40856 2.23226 8.60792C2.25889 8.86427 2.07267 9.09368 1.81632 9.12032C1.55996 9.14695 1.33056 8.96073 1.30392 8.70437C1.27988 8.47302 1.26758 8.23833 1.26758 8.00091C1.26758 7.7635 1.27988 7.5288 1.30392 7.29745C1.33056 7.0411 1.55996 6.85487 1.81632 6.88151Z"
			fill="#148F6A"
		/>
		<path
			d="M2.70084 10.3604C2.59588 10.125 2.31996 10.0193 2.08457 10.1243C1.84918 10.2292 1.74345 10.5052 1.84841 10.7405C2.04118 11.1728 2.2781 11.581 2.55336 11.9592C2.70502 12.1676 2.99689 12.2136 3.20528 12.0619C3.41367 11.9102 3.45966 11.6184 3.308 11.41C3.07076 11.084 2.86675 10.7325 2.70084 10.3604Z"
			fill="#148F6A"
		/>
		<path
			d="M3.93993 12.7965C4.09159 12.5882 4.38347 12.5422 4.59185 12.6938C4.91782 12.9311 5.26932 13.1351 5.64139 13.301C5.87678 13.4059 5.98251 13.6819 5.87755 13.9172C5.77258 14.1526 5.49667 14.2584 5.26128 14.1534C4.82898 13.9606 4.42086 13.7237 4.04264 13.4485C3.83426 13.2968 3.78827 13.0049 3.93993 12.7965Z"
			fill="#148F6A"
		/>
		<path
			d="M11.3309 5.67094C11.5131 5.85319 11.5131 6.14867 11.3309 6.33091L6.99756 10.6642C6.84769 10.8141 6.62126 10.8407 6.44419 10.7441C6.40591 10.7233 6.36993 10.6966 6.33753 10.6642L4.67086 8.99754C4.48862 8.8153 4.48862 8.51982 4.67086 8.33758C4.85311 8.15533 5.14859 8.15533 5.33083 8.33758L6.66756 9.67431L10.6709 5.67094C10.8532 5.4887 11.1486 5.4887 11.3309 5.67094Z"
			fill="#148F6A"
		/>
	</svg>
);

/**
 * Amber progress-alert circle — used for UNSUBMIT / PENDING.
 * Same dashed-arc structure as ProgressCheckIcon but with an exclamation mark.
 */
export const ProgressAlertIcon: React.FC<IconProps> = ({size = 16}) => (
	<svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		{/* Dashed progress arc — same paths as ProgressCheckIcon */}
		<path
			d="M7.29745 1.30392C7.5288 1.27988 7.7635 1.26758 8.00091 1.26758C11.7196 1.26758 14.7343 4.28219 14.7343 8.00091C14.7343 11.7196 11.7196 14.7342 8.00091 14.7342C7.7635 14.7342 7.5288 14.7219 7.29745 14.6979C7.0411 14.6713 6.85487 14.4419 6.88151 14.1855C6.90814 13.9292 7.13755 13.7429 7.3939 13.7696C7.59327 13.7903 7.79579 13.8009 8.00093 13.8009C11.2042 13.8009 13.8009 11.2042 13.8009 8.00091C13.8009 4.79766 11.2042 2.20091 8.00091 2.20091C7.79577 2.20091 7.59327 2.21154 7.3939 2.23226C7.13755 2.25889 6.90814 2.07267 6.88151 1.81632C6.85487 1.55996 7.0411 1.33056 7.29745 1.30392Z"
			fill="#D97706"
		/>
		<path
			d="M5.87755 2.08457C5.98251 2.31996 5.87678 2.59588 5.64139 2.70084C5.26932 2.86675 4.91782 3.07076 4.59185 3.308C4.38347 3.45966 4.09159 3.41367 3.93993 3.20528C3.78827 2.99689 3.83426 2.70502 4.04264 2.55336C4.42086 2.2781 4.82898 2.04118 5.26128 1.84841C5.49667 1.74345 5.77258 1.84918 5.87755 2.08457Z"
			fill="#D97706"
		/>
		<path
			d="M3.308 4.59185C3.45966 4.38347 3.41367 4.09159 3.20528 3.93993C2.99689 3.78827 2.70502 3.83426 2.55336 4.04264C2.2781 4.42086 2.04118 4.82898 1.84842 5.26128C1.74345 5.49667 1.84918 5.77258 2.08457 5.87755C2.31996 5.98251 2.59588 5.87678 2.70084 5.64139C2.86675 5.26932 3.07076 4.91782 3.308 4.59185Z"
			fill="#D97706"
		/>
		<path
			d="M1.81632 6.88151C2.07267 6.90814 2.25889 7.13755 2.23226 7.3939C2.21154 7.59327 2.20091 7.79577 2.20091 8.00091C2.20091 8.20605 2.21154 8.40856 2.23226 8.60792C2.25889 8.86427 2.07267 9.09368 1.81632 9.12032C1.55996 9.14695 1.33056 8.96073 1.30392 8.70437C1.27988 8.47302 1.26758 8.23833 1.26758 8.00091C1.26758 7.7635 1.27988 7.5288 1.30392 7.29745C1.33056 7.0411 1.55996 6.85487 1.81632 6.88151Z"
			fill="#D97706"
		/>
		<path
			d="M2.70084 10.3604C2.59588 10.125 2.31996 10.0193 2.08457 10.1243C1.84918 10.2292 1.74345 10.5052 1.84841 10.7405C2.04118 11.1728 2.2781 11.581 2.55336 11.9592C2.70502 12.1676 2.99689 12.2136 3.20528 12.0619C3.41367 11.9102 3.45966 11.6184 3.308 11.41C3.07076 11.084 2.86675 10.7325 2.70084 10.3604Z"
			fill="#D97706"
		/>
		<path
			d="M3.93993 12.7965C4.09159 12.5882 4.38347 12.5422 4.59185 12.6938C4.91782 12.9311 5.26932 13.1351 5.64139 13.301C5.87678 13.4059 5.98251 13.6819 5.87755 13.9172C5.77258 14.1526 5.49667 14.2584 5.26128 14.1534C4.82898 13.9606 4.42086 13.7237 4.04264 13.4485C3.83426 13.2968 3.78827 13.0049 3.93993 12.7965Z"
			fill="#D97706"
		/>
		{/* Exclamation mark — stem */}
		<path
			d="M7.53 4.8 C7.53 4.52 7.76 4.3 8 4.3 C8.24 4.3 8.47 4.52 8.47 4.8 L8.47 9.2 C8.47 9.48 8.24 9.7 8 9.7 C7.76 9.7 7.53 9.48 7.53 9.2 Z"
			fill="#D97706"
		/>
		{/* Exclamation mark — dot */}
		<path
			d="M8.7 11.5 C8.7 11.114 8.386 10.8 8 10.8 C7.614 10.8 7.3 11.114 7.3 11.5 C7.3 11.886 7.614 12.2 8 12.2 C8.386 12.2 8.7 11.886 8.7 11.5 Z"
			fill="#D97706"
		/>
	</svg>
);

/** Dark note/document icon — used for INVOICE_LINK / INVOICE_UNLINK */
export const NoteIcon: React.FC<IconProps> = ({size = 16}) => (
	<svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M4.66666 4.86666C4.40893 4.86666 4.19999 5.07559 4.19999 5.33333C4.19999 5.59106 4.40893 5.79999 4.66666 5.79999H11.3333C11.5911 5.79999 11.8 5.59106 11.8 5.33333C11.8 5.07559 11.5911 4.86666 11.3333 4.86666H4.66666Z"
			fill="#232334"
		/>
		<path
			d="M4.19999 7.99999C4.19999 7.74226 4.40893 7.53333 4.66666 7.53333H11.3333C11.5911 7.53333 11.8 7.74226 11.8 7.99999C11.8 8.25772 11.5911 8.46666 11.3333 8.46666H4.66666C4.40893 8.46666 4.19999 8.25772 4.19999 7.99999Z"
			fill="#232334"
		/>
		<path
			d="M4.19999 10.6667C4.19999 10.4089 4.40893 10.2 4.66666 10.2H9.33333C9.59106 10.2 9.79999 10.4089 9.79999 10.6667C9.79999 10.9244 9.59106 11.1333 9.33333 11.1333H4.66666C4.40893 11.1333 4.19999 10.9244 4.19999 10.6667Z"
			fill="#232334"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M2 4C2 2.89543 2.89543 2 4 2H12C13.1046 2 14 2.89543 14 4V12C14 13.1046 13.1046 14 12 14H4C2.89543 14 2 13.1046 2 12V4ZM4 2.93333H12C12.5891 2.93333 13.0667 3.4109 13.0667 4V12C13.0667 12.5891 12.5891 13.0667 12 13.0667H4C3.4109 13.0667 2.93333 12.5891 2.93333 12V4C2.93333 3.4109 3.4109 2.93333 4 2.93333Z"
			fill="#232334"
		/>
	</svg>
);

/** Blue reload/reverse icon — used for UNAPPROVE / REVERSE_APPROVAL */
export const ReloadIcon: React.FC<IconProps> = ({size = 16}) => (
	<svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M6.24964 3.96221C6.98987 3.64139 7.80407 3.53026 8.60321 3.64099C9.40234 3.75171 10.1557 4.08001 10.7808 4.59001C11.1147 4.86244 11.5036 5.21373 11.8126 5.50072C11.8242 5.51147 11.8357 5.52213 11.847 5.53267H9.99938C9.74165 5.53267 9.53271 5.74161 9.53271 5.99934C9.53271 6.25707 9.74165 6.46601 9.99938 6.46601H12.866C13.1238 6.46601 13.3327 6.25707 13.3327 5.99934V3.13268C13.3327 2.87495 13.1238 2.66602 12.866 2.66602C12.6083 2.66602 12.3994 2.87495 12.3994 3.13268V4.77196C12.0935 4.4889 11.7101 4.14368 11.3708 3.86681C10.6131 3.24864 9.69995 2.85069 8.7313 2.71648C7.76265 2.58228 6.77574 2.71697 5.87848 3.10585C4.98123 3.49472 4.20816 4.12281 3.6438 4.92143C3.07944 5.72005 2.74552 6.65846 2.67854 7.63407C2.61156 8.60968 2.81411 9.58492 3.26404 10.4532C3.71396 11.3214 4.39394 12.0493 5.22964 12.5571C6.06534 13.065 7.02458 13.3333 8.00248 13.3327C8.26021 13.3325 8.46902 13.1235 8.46887 12.8657C8.46872 12.608 8.25967 12.3992 8.00193 12.3993C7.19516 12.3998 6.40379 12.1785 5.71434 11.7595C5.02489 11.3405 4.4639 10.7401 4.09272 10.0238C3.72153 9.30744 3.55443 8.50287 3.60968 7.69799C3.66494 6.89312 3.94042 6.11893 4.40602 5.46006C4.87162 4.8012 5.5094 4.28303 6.24964 3.96221Z"
			fill="#0066FF"
		/>
	</svg>
);

/** Dark feedback/chat icon — used as the reason/comment icon */
export const FeedbackIcon: React.FC<IconProps> = ({size = 16}) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M6.3 3.5C4.47746 3.5 3 4.97746 3 6.8V12.7125C3 14.3301 4.16392 15.6759 5.69999 15.9581V18.6875C5.69999 18.9513 5.84831 19.1927 6.08365 19.3119C6.31899 19.4312 6.60137 19.4079 6.81408 19.2519L9.11408 17.5644C9.42578 17.3357 9.49307 16.8976 9.26437 16.5859C9.03568 16.2742 8.5976 16.2069 8.2859 16.4356L7.09999 17.3057V15.3125C7.09999 14.9259 6.78659 14.6125 6.39999 14.6125H6.3C5.25066 14.6125 4.4 13.7618 4.4 12.7125V6.8C4.4 5.75066 5.25066 4.9 6.3 4.9H15.4C16.4493 4.9 17.3 5.75066 17.3 6.8V7.59998C17.3 7.98658 17.6134 8.29998 18 8.29998C18.3866 8.29998 18.7 7.98658 18.7 7.59998V6.8C18.7 4.97746 17.2225 3.5 15.4 3.5H6.3Z"
			fill="#232334"
		/>
		<path
			d="M6.59996 6.5C6.21336 6.5 5.89996 6.8134 5.89996 7.2C5.89996 7.5866 6.21336 7.9 6.59996 7.9H15.1C15.4866 7.9 15.8 7.5866 15.8 7.2C15.8 6.8134 15.4866 6.5 15.1 6.5H6.59996Z"
			fill="#232334"
		/>
		<path
			d="M5.89996 10.1C5.89996 9.71343 6.21336 9.40002 6.59996 9.40002H8.59996C8.98656 9.40002 9.29996 9.71343 9.29996 10.1C9.29996 10.4866 8.98656 10.8 8.59996 10.8H6.59996C6.21336 10.8 5.89996 10.4866 5.89996 10.1Z"
			fill="#232334"
		/>
		<path
			d="M13.8 12C13.4134 12 13.1 12.3134 13.1 12.7C13.1 13.0866 13.4134 13.4 13.8 13.4H17.8C18.1866 13.4 18.5 13.0866 18.5 12.7C18.5 12.3134 18.1866 12 17.8 12H13.8Z"
			fill="#232334"
		/>
		<path
			d="M13.1 15.2C13.1 14.8134 13.4134 14.5 13.8 14.5H17.8C18.1866 14.5 18.5 14.8134 18.5 15.2C18.5 15.5866 18.1866 15.9 17.8 15.9H13.8C13.4134 15.9 13.1 15.5866 13.1 15.2Z"
			fill="#232334"
		/>
		<path
			fillRule="evenodd"
			clipRule="evenodd"
			d="M12.3 9.5C11.3611 9.5 10.6 10.2611 10.6 11.2V16.6667C10.6 17.6056 11.3611 18.3667 12.3 18.3667H14.3876L17.5581 21.4981C17.759 21.6964 18.0594 21.7547 18.3199 21.6459C18.5804 21.537 18.75 21.2823 18.75 21V18.3667H19.3C20.2389 18.3667 21 17.6056 21 16.6667V11.2C21 10.2611 20.2389 9.5 19.3 9.5H12.3ZM12 11.2C12 11.0343 12.1343 10.9 12.3 10.9H19.3C19.4657 10.9 19.6 11.0343 19.6 11.2V16.6667C19.6 16.8324 19.4657 16.9667 19.3 16.9667H18.05C17.6634 16.9667 17.35 17.2801 17.35 17.6667V19.3248L15.1669 17.1686C15.0358 17.0392 14.8591 16.9667 14.675 16.9667H12.3C12.1343 16.9667 12 16.8324 12 16.6667V11.2Z"
			fill="#232334"
		/>
	</svg>
);

/** Slate pencil — used for a field edit */
export const PencilIcon: React.FC<IconProps> = ({size = 16}) => (
	<svg width={size} height={size} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path d="M2 14L2.75 11.1L4.9 13.25L2 14Z" fill="#4C4C58" />
		<path d="M3.45 10.4L10.4 3.45L12.55 5.6L5.6 12.55L3.45 10.4Z" fill="#4C4C58" />
		<path
			d="M11.1 2.75L12.35 1.5C12.74 1.11 13.37 1.11 13.76 1.5L14.5 2.24C14.89 2.63 14.89 3.26 14.5 3.65L13.25 4.9L11.1 2.75Z"
			fill="#4C4C58"
		/>
	</svg>
);
